import { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
	name: "",
	price: "",
	category: "Устрицы",
	subcategory: "Россия",
	origin: "",
	availability: "есть",
	details: "",
	image_url: "",
	description: "",
	weight: "",
};

export default function ProductFormModal({
	open,
	mode,
	product,
	onClose,
	onSaved,
}) {
	const [form, setForm] = useState(EMPTY_FORM);
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);

	useEffect(() => {
		if (!open) return;

		if (mode === "edit" && product) {
			setForm({
				name: product.name || "",
				price: product.price != null ? String(product.price) : "",
				category: product.category || "Устрицы",
				subcategory: product.subcategory || "Россия",
				origin: product.origin || "",
				availability: product.availability || "есть",
				details: product.details || "",
				image_url: product.image_url || "",
				description: product.description || "",
				weight: product.weight || "",
			});
		} else {
			setForm(EMPTY_FORM);
		}
	}, [open, mode, product]);

	const handleChange = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose && onClose();
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (
			!form.name ||
			!form.price ||
			!form.category ||
			!form.origin ||
			!form.availability
		) {
			toast.error(
				"Заполните обязательные поля: название, цена, категория, происхождение, наличие"
			);
			return;
		}

		const payload = {
			name: form.name.trim(),
			price: Number(form.price),
			category: form.category,
			subcategory:
				form.category === "Устрицы"
					? form.subcategory
					: form.subcategory || null,
			origin: form.origin.trim(),
			availability: form.availability,
			details: form.details.trim() || null,
			image_url: form.image_url.trim() || null,
			description: form.description.trim() || null,
			weight: form.weight.trim() || null,
		};

		try {
			setSaving(true);
			let res;

			if (mode === "edit" && product?.id) {
				res = await axios.put(`${API}/products/${product.id}`, payload);
				toast.success("Товар обновлён");
			} else {
				res = await axios.post(`${API}/products`, payload);
				toast.success("Товар добавлен");
			}

			onSaved && onSaved(res.data);
			onClose && onClose();
		} catch (error) {
			console.error("Failed to save product:", error);
			toast.error("Не удалось сохранить товар");
		} finally {
			setSaving(false);
		}
	};

	// ==== upload image ====

	const handleImageUpload = async (file) => {
		const fd = new FormData();
		fd.append("image", file); // имя должно совпадать с upload.single('image') на бэке

		try {
			setUploading(true);
			const res = await axios.post(`${API}/upload/product-image`, fd, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			if (res.data?.url) {
				setForm((prev) => ({ ...prev, image_url: res.data.url }));
				toast.success("Изображение загружено");
			} else {
				toast.error("Сервер не вернул URL изображения");
			}
		} catch (err) {
			console.error("upload error:", err);
			toast.error("Ошибка загрузки изображения");
		} finally {
			setUploading(false);
		}
	};

	const handleFileChange = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		if (!file.type.startsWith("image/")) {
			toast.error("Можно загружать только изображения");
			return;
		}

		handleImageUpload(file);
	};

	const isOysters = form.category === "Устрицы";

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
			onClick={handleOverlayClick}
		>
			<div className="bg-white rounded-2xl shadow-xl w-full max-w-xl p-6 relative">
				<button
					onClick={onClose}
					className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 text-gray-500"
				>
					<X className="w-4 h-4" />
				</button>

				<h2 className="text-xl font-semibold mb-4">
					{mode === "edit" ? "Редактировать товар" : "Добавить товар"}
				</h2>

				<form className="space-y-4" onSubmit={handleSubmit}>
					<div>
						<label className="block text-sm font-medium mb-1">Название *</label>
						<Input
							value={form.name}
							onChange={(e) => handleChange("name", e.target.value)}
							placeholder="Устрица Фин де Клер №3"
						/>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium mb-1">
								Цена, ₽ *
							</label>
							<Input
								type="number"
								min="0"
								step="0.01"
								value={form.price}
								onChange={(e) => handleChange("price", e.target.value)}
								placeholder="1200"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium mb-1">
								Вес / фасовка
							</label>
							<Input
								value={form.weight}
								onChange={(e) => handleChange("weight", e.target.value)}
								placeholder="1 кг / 12 шт"
							/>
						</div>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium mb-1">
								Категория *
							</label>
							<Select
								value={form.category}
								onValueChange={(v) => handleChange("category", v)}
							>
								<SelectTrigger>
									<SelectValue placeholder="Выберите категорию" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="Устрицы">Устрицы</SelectItem>
									<SelectItem value="Живые морепродукты">
										Живые морепродукты
									</SelectItem>
									<SelectItem value="Замороженная рыба">
										Замороженная рыба
									</SelectItem>
									<SelectItem value="Разное">Разное</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{isOysters && (
							<div>
								<label className="block text-sm font-medium mb-1">
									Происхождение (фильтр)
								</label>
								<Select
									value={form.subcategory}
									onValueChange={(v) => handleChange("subcategory", v)}
								>
									<SelectTrigger>
										<SelectValue placeholder="Страна" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="Россия">Россия</SelectItem>
										<SelectItem value="Африка">Африка</SelectItem>
										<SelectItem value="Азия">Азия</SelectItem>
									</SelectContent>
								</Select>
							</div>
						)}
					</div>

					<div>
						<label className="block text-sm font-medium mb-1">
							Страна / регион происхождения (🌍) *
						</label>
						<Input
							value={form.origin}
							onChange={(e) => handleChange("origin", e.target.value)}
							placeholder="Франция, Нормандия"
						/>
					</div>

					<div>
						<label className="block text-sm font-medium mb-1">Наличие *</label>
						<Select
							value={form.availability}
							onValueChange={(v) => handleChange("availability", v)}
						>
							<SelectTrigger>
								<SelectValue placeholder="Наличие" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="есть">В наличии</SelectItem>
								<SelectItem value="ожидаем">Ожидается</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div>
						<label className="block text-sm font-medium mb-1">
							Краткие детали
						</label>
						<Input
							value={form.details}
							onChange={(e) => handleChange("details", e.target.value)}
							placeholder="Размер №3, 12 шт/кг"
						/>
					</div>

					<div>
						<label className="block text-sm font-medium mb-1">Описание</label>
						<Textarea
							rows={3}
							value={form.description}
							onChange={(e) => handleChange("description", e.target.value)}
							placeholder="Расскажите подробнее о вкусе, текстуре и т.д."
						/>
					</div>

					{/* === БЛОК ИЗОБРАЖЕНИЯ С АПЛОАДОМ === */}
					<div>
						<label className="block text-sm font-medium mb-1">
							Изображение
						</label>

						<input type="file" accept="image/*" onChange={handleFileChange} />

						{form.image_url && (
							<img
								src={form.image_url}
								alt="Превью товара"
								className="h-24 w-24 mt-2 rounded-md border object-cover"
							/>
						)}
					</div>

					<div className="flex justify-end gap-3 pt-4">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={saving}
						>
							Отмена
						</Button>
						<Button type="submit" disabled={saving}>
							{saving
								? "Сохранение..."
								: mode === "edit"
								? "Сохранить изменения"
								: "Добавить товар"}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
